import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

/**
 * Realtime sync hook (IDEX "Global" replication clone).
 *
 * Connects to the backend's socket.io server on the same origin (nginx
 * proxies /socket.io to the API container; devserver.ts serves both), listens
 * for the mission:* events broadcast by the change/mission routes, and reports
 * connection state so the UI can show a live indicator.
 */

export type RealtimeEvent =
  | "mission:changed"
  | "mission:updated"
  | "mission:created"
  | "mission:deleted";

const MISSION_EVENTS: RealtimeEvent[] = [
  "mission:changed",
  "mission:updated",
  "mission:created",
  "mission:deleted",
];

export function useRealtime(
  onEvent: (event: RealtimeEvent, payload: unknown) => void
): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  // Keep the latest callback in a ref so the socket effect runs exactly once
  // (callers can pass inline closures without re-triggering connect/disconnect)
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    const socket: Socket = io(); // same origin

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));

    for (const event of MISSION_EVENTS) {
      socket.on(event, (payload: unknown) => onEventRef.current(event, payload));
    }

    return () => {
      socket.close();
    };
  }, []);

  return { connected };
}
